import { QueryClient } from '@tanstack/react-query';
import { isHttpAbortError, toUserFacingHttpError } from '@utils/httpErrors';

const QUERY_DEFAULTS = {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    /** Recovery coordinator refetches after wake; avoid duplicate storms with reconnect. */
    refetchOnReconnect: false,
    networkMode: 'online',
    staleTime: Infinity,
    retry: (failureCount, error) => {
        if (isHttpAbortError(error)) return false;
        if (error?.response?.status === 401) return false;
        return failureCount < 1;
    },
};

const MUTATION_DEFAULTS = {
    retry: (failureCount, error) => {
        if (isHttpAbortError(error)) return false;
        const status = error?.response?.status;
        if (status && status >= 400 && status < 500) return false;
        return false;
    },
};

export const appQueryClient = new QueryClient({
    defaultOptions: {
        queries: { ...QUERY_DEFAULTS },
        mutations: { ...MUTATION_DEFAULTS },
    },
});

/**
 * `setDefaultOptions` replaces the whole defaults object instead of merging, so every
 * option has to be re-supplied. Changing only `networkMode` would silently drop
 * `staleTime`, `retry` and the `refetchOn*` flags back to React Query's defaults, which
 * turns a wake into a refetch storm that also retries 401s.
 */
function applyQueryNetworkMode(networkMode) {
    appQueryClient.setDefaultOptions({
        queries: { ...QUERY_DEFAULTS, networkMode },
        mutations: { ...MUTATION_DEFAULTS },
    });
}

let queriesPausedForRecovery = false;

/** Cancel in-flight queries/mutations and drop cache (logout, session expired, sign-in mount). */
export function resetAppQueryClientCaches() {
    void appQueryClient.cancelQueries({ predicate: () => true });
    appQueryClient.clear();
}

export function pauseAppQueriesForRecovery() {
    if (queriesPausedForRecovery) return;
    queriesPausedForRecovery = true;
    // Switching networkMode to 'offlineFirst' prevents React Query from
    // automatically firing fetches while the app is recovering (e.g. wake/reconnect).
    applyQueryNetworkMode('offlineFirst');
}

export function resumeAppQueriesAfterRecovery() {
    if (!queriesPausedForRecovery) return;
    queriesPausedForRecovery = false;
    // Restore the normal networkMode so queries can fetch again.
    applyQueryNetworkMode('online');
}

/**
 * Refetch active queries in small batches so wake recovery does not saturate the browser connection pool.
 */
export async function refetchActiveQueriesBatched(concurrency = 3) {
    const active = appQueryClient.getQueryCache().findAll({ type: 'active' });
    const limit = Math.max(1, concurrency);
    for (let i = 0; i < active.length; i += limit) {
        const batch = active.slice(i, i + limit);
        await Promise.allSettled(batch.map((query) => query.fetch()));
    }
}

/** @deprecated Use refetchActiveQueriesBatched */
export async function refetchActiveQueriesAfterHttpRecovery() {
    await refetchActiveQueriesBatched(3);
}

export { toUserFacingHttpError };
